/** @jsx jsx */
import { memo, useCallback, useMemo, type Ref, type MutableRefObject, type ReactNode } from 'react';
import { jsx } from '@compiled/react';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { ButtonMenuItem } from '@atlaskit/side-nav-items/button-menu-item';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { FormattedMessage } from '@atlassian/jira-intl';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics/cross-flow/hooks';
import { SURFACE_ANALYTICS_PRODUCT_MAP } from '@atlassian/surface-analytics/ccp-products';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { useEffectWithoutConcurrency } from '@atlassian/jira-react-strict-mode/src/useEffectWithoutConcurrency.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import AdControlProvider from '@atlassian/ad-control-toolkit/ad-control-provider';
import { useRoadmapUpsellAdAutoDismissal } from './useRoadmapUpsellAdControls.tsx';
import { useOpenCrossFlow } from './useOpenCrossFlow.tsx';
import { RoadmapUpsellAdControlsButton } from './RoadmapUpsellAdControlsButton.tsx';

const TryLozenge = () => {
	return (
		<Lozenge appearance="discovery">
			<FormattedMessage
				id="polaris.try-lozenge.label"
				defaultMessage="Try"
				description="Text displayed in the try lozenge"
			/>
		</Lozenge>
	);
};

type RoadmapsUpsellProps = {
	// Nav4DragAndDropMenuItemProps
	draggableRef?: Ref<HTMLAnchorElement | HTMLButtonElement> | null;
	dropIndicator?: ReactNode;
	hasDragIndicator?: boolean;
	isDragging?: boolean;
	visualContentRef?: MutableRefObject<HTMLDivElement | null>;
	// From useConfigurableMessageAndIconMap
	formattedMessage: string;
	icon: ReactNode;
};

export const RoadmapsUpsell: React.FC<RoadmapsUpsellProps> = (props) => {
	const licensedProducts = useLicensedProducts();
	const hasJpd = licensedProducts['jira-product-discovery'] === true;

	const errorBoundaryAttributes = useMemo(
		() => ({
			errorContext: 'Failed rendering JpdRoadmapNav',
			isJpdLicensed: hasJpd,
		}),
		[hasJpd],
	);

	return (
		<JSErrorBoundary
			fallback="unmount"
			id="jpdRoadmapNav"
			packageName="jiraRoadmapNav"
			teamName="JPD - Juno"
			attributes={errorBoundaryAttributes}
		>
			<JpdRoadmapNavInternal {...props} hasJpd={hasJpd} />
		</JSErrorBoundary>
	);
};

type RoadmapsUpsellInternalProps = RoadmapsUpsellProps & {
	hasJpd: boolean;
};
const JpdRoadmapNavInternal = memo((props: RoadmapsUpsellInternalProps) => {
	const {
		draggableRef,
		dropIndicator,
		hasDragIndicator,
		isDragging,
		visualContentRef,
		formattedMessage,
		icon,
		hasJpd,
	} = props;
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const dragRef = draggableRef as Ref<HTMLButtonElement>;

	useRoadmapUpsellAdAutoDismissal();

	const { fireViewedUIEvent, fireInteractedUIEvent } = useFireCrossFlowAnalyticsEvents();
	const openCrossFlow = useOpenCrossFlow();

	const handleClick = useCallback(() => {
		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'roadmaps',
			},
			{
				recommendedProductIds: [SURFACE_ANALYTICS_PRODUCT_MAP.products['jira-product-discovery']],
				targetProductId: SURFACE_ANALYTICS_PRODUCT_MAP.products['jira-product-discovery'],
				source: 'jiraNavigationSection',
			},
			{
				isJpdLicensed: hasJpd,
			},
		);
		openCrossFlow();
	}, [fireInteractedUIEvent, hasJpd, openCrossFlow]);

	useEffectWithoutConcurrency(() => {
		fireViewedUIEvent(
			{
				action: 'viewed',
				actionSubject: 'button',
				actionSubjectId: 'jiraNavigation',
			},
			{
				recommendedProductIds: [SURFACE_ANALYTICS_PRODUCT_MAP.products['jira-product-discovery']],
				source: 'jiraNavigationSection',
			},
			{
				isJpdLicensed: hasJpd,
			},
		);
	}, [fireViewedUIEvent, hasJpd]);

	return (
		<ContextualAnalyticsData sourceType={SCREEN} sourceName="roadmapOnNav">
			<AdControlProvider>
				<ButtonMenuItem
					elemBefore={icon}
					elemAfter={<TryLozenge />}
					ref={dragRef}
					hasDragIndicator={hasDragIndicator}
					actionsOnHover={<RoadmapUpsellAdControlsButton />}
					dropIndicator={dropIndicator}
					isDragging={isDragging}
					visualContentRef={visualContentRef}
					onClick={handleClick}
					testId="jira-roadmap-in-nav.jpd-roadmap-nav-button"
				>
					{formattedMessage}
				</ButtonMenuItem>
			</AdControlProvider>

			<FireScreenAnalytics
				attributes={{
					name: 'roadmapOnNav',
					isJpdLicensed: hasJpd,
				}}
			/>
		</ContextualAnalyticsData>
	);
});
